import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { ArrowLeft, RefreshCw, Play, Download, RotateCcw, Check, X, Plus, Edit2, Trash2, Save, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

// ── Status config ────────────────────────────────────────────────────────────

const STATUS_MAP = {
  draft:                  { label: 'Draft',              bg: '#f1f5f9', color: '#475569' },
  sampling:               { label: 'Sampling…',          bg: '#eff6ff', color: '#1e40af' },
  codelist_merge_pending: { label: 'Choose Merge Level', bg: '#fff7ed', color: '#c2410c' },
  draft_codelist_ready:   { label: 'Draft Codelist Ready', bg: '#fffbeb', color: '#92400e' },
  processing:             { label: 'Processing',         bg: '#eef2ff', color: '#3730a3' },
  rerunning:              { label: 'Processing…',        bg: '#eef2ff', color: '#3730a3' },
  completed:              { label: 'Completed',          bg: '#f0fdf4', color: '#065f46' },
  failed:                 { label: 'Failed',             bg: '#fff1f2', color: '#9f1239' },
};

// ── Shared primitives ────────────────────────────────────────────────────────

const inp = (extra = {}) => ({
  width: '100%', padding: '8px 12px', border: '1.5px solid #cbd5e1', borderRadius: 8,
  fontSize: 13, outline: 'none', boxSizing: 'border-box', color: '#334155',
  background: '#fff', fontFamily: 'inherit', ...extra,
});
const btn = (bg = '#0f172a', color = '#fff', extra = {}) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: bg, color,
  border: (bg === 'transparent' || bg === 'none') ? '1.5px solid #e2e8f0' : 'none',
  borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0, ...extra,
});
const card = (extra = {}) => ({
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', ...extra,
});
const labelCss = { fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' };

function StatusChip({ status }) {
  const s = STATUS_MAP[status] || { label: status, bg: '#f1f5f9', color: '#64748b' };
  return <span style={{ display: 'inline-block', padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>;
}

function AlertBox({ type = 'info', children, onClose }) {
  const m = { info: { bg: '#eff6ff', b: '#3b82f6', c: '#1e40af' }, success: { bg: '#f0fdf4', b: '#10b981', c: '#065f46' }, warning: { bg: '#fffbeb', b: '#f59e0b', c: '#78350f' }, error: { bg: '#fff1f2', b: '#f43f5e', c: '#9f1239' } };
  const s = m[type] || m.info;
  return (
    <div style={{ background: s.bg, borderLeft: `4px solid ${s.b}`, borderRadius: 8, padding: '10px 14px', fontSize: 13, color: s.c, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: s.c, fontSize: 16, padding: 0 }}>×</button>}
    </div>
  );
}

function Modal({ open, onClose, title, subtitle, icon, children, actions }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(2px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 520, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', fontFamily: 'inherit' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '24px 24px 20px' }}>
          {icon && <div style={{ width: 44, height: 44, borderRadius: 12, background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</div>}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 2, lineHeight: 1, marginTop: 2 }}><X size={18} /></button>
        </div>
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px 24px' }}>{children}</div>
        {actions && <div style={{ borderTop: '1px solid #f1f5f9', background: '#f8fafc', padding: '14px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{actions}</div>}
      </div>
    </div>
  );
}

function Chip({ label, color, onDelete }) {
  const m = { primary: { bg: '#eff6ff', c: '#1e40af' }, success: { bg: '#f0fdf4', c: '#065f46' }, warning: { bg: '#fffbeb', c: '#78350f' }, error: { bg: '#fff1f2', c: '#9f1239' }, default: { bg: '#f1f5f9', c: '#475569' } };
  const s = m[color] || m.default;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 20, fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.c, flexShrink: 0 }}>
      {label}
      {onDelete && <span onClick={onDelete} style={{ cursor: 'pointer', marginLeft: 2, lineHeight: 1 }}>×</span>}
    </span>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}>
      <div onClick={() => !disabled && onChange({ target: { checked: !checked } })} style={{ width: 36, height: 20, borderRadius: 10, background: checked ? '#0f172a' : '#cbd5e1', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
        <div style={{ position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
      </div>
    </label>
  );
}

function Accordion({ title, badge, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer', background: open ? '#f8fafc' : '#fff', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>{title}</div>
        {open ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
      </div>
      {open && <div style={{ padding: '12px 14px', borderTop: '1px solid #e2e8f0' }}>{children}</div>}
    </div>
  );
}

function ProgressBar({ value, message }) {
  return (
    <div>
      <div style={{ background: '#e2e8f0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 4, background: '#3b82f6', width: `${value || 0}%`, transition: 'width 0.4s' }} />
      </div>
      <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>{value || 0}% {message ? `— ${message}` : ''}</div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function OECodingDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [codelists, setCodelists] = useState([]);
  const [discoveredThemes, setDiscoveredThemes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [sentimentColumn, setSentimentColumn] = useState('');
  const [sentimentRunning, setSentimentRunning] = useState(false);
  const [sentimentResults, setSentimentResults] = useState([]);
  const [sentimentSummary, setSentimentSummary] = useState(null);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [approveForm, setApproveForm] = useState({ code: '', label: '', nets: '', super_nets: '' });

  const [othersData, setOthersData] = useState(null);
  const [othersLoading, setOthersLoading] = useState(false);
  const [recodeActions, setRecodeActions] = useState({});
  const [newCodeDialog, setNewCodeDialog] = useState({ open: false, textKey: '', text: '', result_ids: [] });
  const [newCodeForm, setNewCodeForm] = useState({ code: '', label: '' });
  const [recodePrompt, setRecodePrompt] = useState('');
  const [recodeCodes, setRecodeCodes] = useState('990');
  const [recodeRunning, setRecodeRunning] = useState(false);
  const [recodeAutoCreate, setRecodeAutoCreate] = useState(false);

  const [llmInteractions, setLlmInteractions] = useState([]);
  const [llmLoading, setLlmLoading] = useState(false);
  const [sessionInstruction, setSessionInstruction] = useState('');
  const [sessionRerunning, setSessionRerunning] = useState(false);

  const [editableCodelists, setEditableCodelists] = useState([]);
  const [sampleResults, setSampleResults] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draftEditDialogOpen, setDraftEditDialogOpen] = useState(false);
  const [editingDraftCode, setEditingDraftCode] = useState(null);
  const [draftEditForm, setDraftEditForm] = useState({ code: '', codelist: '', nets: '', super_nets: '', column_group: '' });

  const fetchJob = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/`);
      setJob(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load job details');
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  const fetchCodelists = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/codelists/`);
      setCodelists(response.data || []);
    } catch (err) { console.error('Failed to load codelists:', err); }
  }, [jobId]);

  const fetchDiscoveredThemes = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/discovered-themes/`);
      setDiscoveredThemes(response.data?.themes || []);
    } catch (err) { console.error('Failed to load discovered themes:', err); }
  }, [jobId]);

  const fetchLLMInteractions = useCallback(async () => {
    if (!jobId) return;
    setLlmLoading(true);
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/llm-interactions/`);
      setLlmInteractions(response.data || []);
    } catch (err) { console.error('Failed to load LLM interactions:', err); }
    finally { setLlmLoading(false); }
  }, [jobId]);

  const handleRerunWithInstruction = async () => {
    if (!sessionInstruction.trim()) return;
    setSessionRerunning(true);
    try {
      await api.post(`oe_coding/jobs/${jobId}/rerun-with-context/`, { instruction: sessionInstruction.trim() });
      setSessionInstruction(''); setLlmInteractions([]);
      await fetchJob();
    } catch (err) { alert('Failed to rerun: ' + (err.response?.data?.error || err.message)); }
    finally { setSessionRerunning(false); }
  };

  const [regenerating, setRegenerating] = useState(false);
  const [mergeLevel, setMergeLevel] = useState('medium');
  const [phrasing, setPhrasing] = useState('respondent');
  const [applyingMerge, setApplyingMerge] = useState(false);

  const handleApplyMerge = async () => {
    setApplyingMerge(true);
    try {
      await api.post(`oe_coding/jobs/${jobId}/apply-merge/`, { merge_level: mergeLevel, phrasing });
      await fetchJob();
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
    finally { setApplyingMerge(false); }
  };

  const handleRegenerateCodelist = async () => {
    if (!window.confirm('This will delete the current draft codelist and re-run Stage 1 from scratch using all responses. Continue?')) return;
    setRegenerating(true);
    try {
      await api.post(`oe_coding/jobs/${jobId}/regenerate-codelist/`, { instruction: sessionInstruction.trim() });
      setSessionInstruction(''); setLlmInteractions([]);
      await fetchJob();
    } catch (err) { alert('Failed to regenerate: ' + (err.response?.data?.error || err.message)); }
    finally { setRegenerating(false); }
  };

  useEffect(() => {
    if (jobId) { fetchJob(); fetchCodelists(); fetchDiscoveredThemes(); }
  }, [jobId, fetchJob, fetchCodelists, fetchDiscoveredThemes]);

  const fetchSampleResults = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/sample-results/`);
      setSampleResults(response.data?.results || []);
    } catch (err) { console.error('Failed to load sample results:', err); }
  }, [jobId]);

  useEffect(() => {
    if (job?.status === 'processing' || job?.status === 'sampling' || job?.status === 'rerunning') {
      const interval = setInterval(fetchJob, 5000);
      return () => clearInterval(interval);
    }
    if (job?.status === 'completed' || job?.status === 'failed') { fetchCodelists(); fetchDiscoveredThemes(); }
    if (job?.status === 'draft_codelist_ready' || job?.status === 'codelist_merge_pending') { fetchCodelists(); fetchSampleResults(); }
  }, [job?.status, fetchJob, fetchCodelists, fetchDiscoveredThemes, fetchSampleResults]);

  useEffect(() => {
    if (['draft_codelist_ready', 'codelist_merge_pending'].includes(job?.status) && codelists.length > 0 && editableCodelists.length === 0) {
      const editable = codelists.filter(c => c.column_group !== 'Standard' && c.code !== 98 && c.code !== 99).map(c => ({
        code: c.code, codelist: c.codelist, nets: c.nets || '', super_nets: c.super_nets || '', column_group: c.column_group || 'General'
      }));
      setEditableCodelists(editable);
    }
  }, [job?.status, codelists, editableCodelists.length]);

  const handleStartJob = async () => {
    try { await api.post(`oe_coding/jobs/${jobId}/start/`); fetchJob(); }
    catch (err) { setError('Failed to start job: ' + (err.response?.data?.error || err.message)); }
  };

  const handleDownload = async () => {
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/download/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', job.output_file_name || 'output.xlsx');
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { setError('Failed to download file'); }
  };

  const [downloadingSpss, setDownloadingSpss] = useState(false);
  const handleDownloadSpss = async () => {
    setDownloadingSpss(true);
    try {
      const response = await api.get(`oe_coding/jobs/${jobId}/download-spss/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', (job.output_file_name || 'output.xlsx').replace('.xlsx', '.sav'));
      document.body.appendChild(link); link.click(); link.remove();
    } catch (err) { setError('Failed to generate SPSS file'); }
    finally { setDownloadingSpss(false); }
  };

  const handleDownloadDraftCodelist = () => {
    const data = editableCodelists.length > 0 ? editableCodelists : codelists;
    if (data.length === 0) return;
    const groups = {};
    data.forEach(entry => { const g = entry.column_group || 'General'; if (!groups[g]) groups[g] = []; groups[g].push(entry); });
    const wb = XLSX.utils.book_new();
    Object.entries(groups).forEach(([groupName, entries]) => {
      const rows = entries.map(e => ({ Code: e.code, Codelist: e.codelist, Nets: e.nets || '', 'Super Nets': e.super_nets || '' }));
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, groupName.substring(0, 31));
    });
    XLSX.writeFile(wb, `${job.name || 'codelist'}_draft_codelist.xlsx`);
  };

  const [pendingAssign, setPendingAssign] = useState({});
  const [savingAssign, setSavingAssign] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    api.get('user-list/').then(r => setAllUsers(r.data || [])).catch(() => {});
  }, []);

  const handleShare = async (action, email) => {
    setShareLoading(true);
    try {
      await api.post(`oe_coding/jobs/${jobId}/share/`, { action, email });
      await fetchJob();
      if (action === 'add') { setShareEmail(''); setUserSearch(''); }
    } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
    finally { setShareLoading(false); }
  };

  const handleRerunJob = async () => {
    if (!window.confirm('This will clear all existing results and re-process the data. Continue?')) return;
    try { await api.post(`oe_coding/jobs/${jobId}/rerun/`); fetchJob(); }
    catch (err) { setError('Failed to re-run job: ' + (err.response?.data?.error || err.message)); }
  };

  const handleForceRestart = async () => {
    if (!window.confirm('The job appears stuck. This will clear all results and restart from the beginning. Continue?')) return;
    try {
      // Try the dedicated force-restart endpoint first
      try {
        await api.post(`oe_coding/jobs/${jobId}/force-restart/`);
      } catch (e) {
        // Fallback: PATCH status to failed so the rerun endpoint accepts it
        await api.patch(`oe_coding/jobs/${jobId}/`, { status: 'failed' });
        await api.post(`oe_coding/jobs/${jobId}/rerun/`);
      }
      fetchJob();
    } catch (err) { setError('Failed to force-restart: ' + (err.response?.data?.error || err.message)); }
  };

  const fetchOthersResponses = async () => {
    setOthersLoading(true);
    try { const res = await api.get(`oe_coding/jobs/${jobId}/others-responses/`); setOthersData(res.data); }
    catch (err) { setError('Failed to fetch others: ' + (err.response?.data?.error || err.message)); }
    finally { setOthersLoading(false); }
  };

  const handleAssignExistingCode = (textKey, resultIds, code) => setRecodeActions(prev => ({ ...prev, [textKey]: { code: parseInt(code), result_ids: resultIds } }));

  const handleOpenNewCodeDialog = (textKey, text, resultIds) => {
    const maxCode = othersData?.codelists?.length ? Math.max(...othersData.codelists.map(c => c.code).filter(c => c < 990)) : 0;
    setNewCodeForm({ code: String(maxCode + 1), label: text });
    setNewCodeDialog({ open: true, textKey, text, result_ids: resultIds });
  };

  const handleSaveNewCode = () => {
    const { textKey, result_ids } = newCodeDialog;
    const code = parseInt(newCodeForm.code);
    if (!code || !newCodeForm.label.trim()) return;
    setRecodeActions(prev => ({ ...prev, [textKey]: { code, new_code_label: newCodeForm.label.trim(), result_ids } }));
    setNewCodeDialog({ open: false, textKey: '', text: '', result_ids: [] });
  };

  const handleSubmitRecodes = async () => {
    const actions = Object.values(recodeActions).filter(a => a.code && a.result_ids?.length);
    if (actions.length === 0) return;
    try {
      const res = await api.post(`oe_coding/jobs/${jobId}/recode-others/`, { actions });
      alert(res.data.message); setRecodeActions({}); fetchOthersResponses(); fetchCodelists();
    } catch (err) { setError('Failed to recode: ' + (err.response?.data?.error || err.message)); }
  };

  const handleRecodeByCode = async () => {
    const codes = recodeCodes.split(',').map(c => parseInt(c.trim())).filter(c => !isNaN(c));
    if (codes.length === 0) return;
    setRecodeRunning(true);
    try {
      const res = await api.post(`oe_coding/jobs/${jobId}/recode-by-code/`, { codes_to_recode: codes, custom_prompt: recodePrompt, auto_create: recodeAutoCreate });
      alert(res.data.message); fetchJob();
    } catch (err) { setError('Failed to start recode: ' + (err.response?.data?.error || err.message)); setRecodeRunning(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  const handleOpenApproveDialog = (theme) => {
    setSelectedTheme(theme);
    const maxCode = Math.max(...codelists.map(c => c.code || 0), 0);
    setApproveForm({ code: (maxCode + 1).toString(), label: theme.suggested_label, nets: '', super_nets: '' });
    setApproveDialogOpen(true);
  };

  const handleApproveTheme = async () => {
    if (!selectedTheme || !approveForm.code) return;
    try {
      await api.post(`oe_coding/jobs/${jobId}/approve-theme/`, { theme_id: selectedTheme.id, code: parseInt(approveForm.code), label: approveForm.label, nets: approveForm.nets, super_nets: approveForm.super_nets });
      setApproveDialogOpen(false); setSelectedTheme(null); fetchCodelists(); fetchDiscoveredThemes();
    } catch (err) { setError('Failed to approve theme: ' + (err.response?.data?.error || err.message)); }
  };

  const handleRejectTheme = async (theme) => {
    if (!window.confirm(`Reject theme "${theme.suggested_label}"?`)) return;
    try {
      await api.post(`oe_coding/jobs/${jobId}/reject-theme/`, { theme_id: theme.id, assign_code_99: false });
      fetchDiscoveredThemes();
    } catch (err) { setError('Failed to reject theme: ' + (err.response?.data?.error || err.message)); }
  };

  const handleEditDraftCode = (entry) => {
    setEditingDraftCode(entry);
    setDraftEditForm({ code: entry.code.toString(), codelist: entry.codelist, nets: entry.nets || '', super_nets: entry.super_nets || '', column_group: entry.column_group || 'General' });
    setDraftEditDialogOpen(true);
  };

  const handleAddDraftCode = () => {
    const maxCode = editableCodelists.length > 0 ? Math.max(...editableCodelists.map(c => c.code)) : 0;
    setEditingDraftCode(null);
    setDraftEditForm({ code: (maxCode + 1).toString(), codelist: '', nets: '', super_nets: '', column_group: editableCodelists[0]?.column_group || 'General' });
    setDraftEditDialogOpen(true);
  };

  const handleSaveDraftCodeEdit = () => {
    const codeNum = parseInt(draftEditForm.code);
    if (!codeNum || !draftEditForm.codelist.trim()) return;
    const newEntry = { code: codeNum, codelist: draftEditForm.codelist.trim(), nets: draftEditForm.nets.trim(), super_nets: draftEditForm.super_nets.trim(), column_group: draftEditForm.column_group || 'General' };
    if (editingDraftCode) { setEditableCodelists(prev => prev.map(c => c.code === editingDraftCode.code ? newEntry : c)); }
    else { setEditableCodelists(prev => [...prev, newEntry].sort((a, b) => a.code - b.code)); }
    setHasUnsavedChanges(true); setDraftEditDialogOpen(false);
  };

  const handleDeleteDraftCode = (code) => { setEditableCodelists(prev => prev.filter(c => c.code !== code)); setHasUnsavedChanges(true); };

  const handleSaveDraftCodelist = async () => {
    try {
      await api.post(`oe_coding/jobs/${jobId}/update-draft-codelist/`, { entries: editableCodelists });
      setHasUnsavedChanges(false); fetchCodelists();
    } catch (err) { setError('Failed to save codelist: ' + (err.response?.data?.error || err.message)); }
  };

  const handleRunFullCoding = async () => {
    if (hasUnsavedChanges) { if (!window.confirm('You have unsaved changes. Save and run full coding?')) return; await handleSaveDraftCodelist(); }
    try { await api.post(`oe_coding/jobs/${jobId}/run-full-coding/`); fetchJob(); }
    catch (err) { setError('Failed to start full coding: ' + (err.response?.data?.error || err.message)); }
  };

  const fetchSentiment = useCallback(async (colName) => {
    if (!jobId) return;
    try {
      const url = colName ? `oe_coding/jobs/${jobId}/sentiment/?column_name=${encodeURIComponent(colName)}` : `oe_coding/jobs/${jobId}/sentiment/`;
      const response = await api.get(url);
      setSentimentSummary(response.data.summary); setSentimentResults(response.data.results || []);
      setSentimentRunning((response.data.progress_message || '').includes('Running sentiment'));
    } catch (err) { console.error('Failed to load sentiment:', err); }
  }, [jobId]);

  const handleRunSentiment = async () => {
    if (!sentimentColumn) return;
    setSentimentRunning(true); setSentimentResults([]); setSentimentSummary(null);
    try {
      await api.post(`oe_coding/jobs/${jobId}/run-sentiment/`, { column_name: sentimentColumn });
      const pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`oe_coding/jobs/${jobId}/sentiment/?column_name=${encodeURIComponent(sentimentColumn)}`);
          setSentimentSummary(response.data.summary); setSentimentResults(response.data.results || []);
          if (!(response.data.progress_message || '').includes('Running sentiment')) { setSentimentRunning(false); clearInterval(pollInterval); fetchJob(); }
        } catch (e) { clearInterval(pollInterval); setSentimentRunning(false); }
      }, 5000);
    } catch (err) { setSentimentRunning(false); setError('Failed to start emotion analysis: ' + (err.response?.data?.error || err.message)); }
  };

  useEffect(() => {
    if (activeTab === 4 && job?.status === 'completed') {
      if (!sentimentColumn && job.target_columns?.length > 0) setSentimentColumn(job.target_columns[0]);
      if (sentimentColumn) fetchSentiment(sentimentColumn);
    }
  }, [activeTab, sentimentColumn, job?.status, job?.target_columns, fetchSentiment]);

  // ── Early returns ────────────────────────────────────────────────────────────

  if (isLoading) return (
    <div style={{ padding: '60px 28px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading job…</div>
  );

  if (!job) return (
    <div style={{ padding: '40px 28px' }}>
      <AlertBox type="error">Job not found.</AlertBox>
      <button style={{ ...btn('transparent', '#475569'), marginTop: 12 }} onClick={() => navigate('/oe-coding')}><ArrowLeft size={13} /> Back to Jobs</button>
    </div>
  );

  // ── Tab definitions ──────────────────────────────────────────────────────────

  const TABS = [
    { label: 'Configuration' },
    { label: 'Draft Codelist', badge: hasUnsavedChanges ? '!' : null, badgeColor: '#f43f5e', disabled: !['codelist_merge_pending', 'draft_codelist_ready', 'processing', 'completed'].includes(job.status) },
    { label: 'Codelists' },
    { label: 'Discovered Themes', badge: discoveredThemes.length > 0 ? discoveredThemes.length : null, badgeColor: '#f59e0b' },
    { label: 'Emotion Analysis', disabled: job.status !== 'completed' },
    { label: 'Others Review', disabled: job.status !== 'completed' },
    { label: 'AI Session', badge: llmInteractions.length > 0 ? llmInteractions.length : null, badgeColor: '#3b82f6' },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ background: '#0f172a', borderRadius: 12, padding: '18px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <button onClick={() => navigate('/oe-coding')} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 6, color: '#cbd5e1', cursor: 'pointer', padding: '4px 10px', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 10 }}>
              <ArrowLeft size={12} /> Back to Jobs
            </button>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>{job.name}</div>
            {job.description && <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 3 }}>{job.description}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <button style={btn('rgba(255,255,255,0.1)', '#e2e8f0', { border: '1px solid rgba(255,255,255,0.15)' })} onClick={fetchJob}><RefreshCw size={13} /> Refresh</button>
            {job.status === 'draft' && (
              <button style={btn('#10b981', '#fff')} onClick={handleStartJob}><Play size={13} /> Start Processing</button>
            )}
            {job.status === 'draft_codelist_ready' && (
              <button style={btn('#f59e0b', '#fff')} onClick={handleRunFullCoding}><Play size={13} /> Run Full Coding</button>
            )}
            {job.status === 'completed' && job.output_file && (<>
              <button style={btn('#10b981', '#fff')} onClick={handleDownload}><Download size={13} /> Download Excel</button>
              <button style={btn('rgba(255,255,255,0.1)', '#e2e8f0', { border: '1px solid rgba(255,255,255,0.15)' })} onClick={handleDownloadSpss} disabled={downloadingSpss}>
                <Download size={13} /> {downloadingSpss ? 'Generating…' : 'Download SPSS'}
              </button>
            </>)}
            {(job.status === 'completed' || job.status === 'failed') && (
              <button style={btn('rgba(255,255,255,0.1)', '#fbbf24', { border: '1px solid rgba(255,255,255,0.15)' })} onClick={handleRerunJob}><RotateCcw size={13} /> Re-run</button>
            )}
            {job.status === 'processing' && (
              <button style={btn('rgba(239,68,68,0.15)', '#ef4444', { border: '1px solid rgba(239,68,68,0.3)' })} onClick={handleForceRestart}><RotateCcw size={13} /> Force Restart</button>
            )}
            <button style={btn('rgba(255,255,255,0.1)', '#e2e8f0', { border: '1px solid rgba(255,255,255,0.15)' })} onClick={() => setShareOpen(o => !o)}>⇄ Share</button>
          </div>
        </div>
        {shareOpen && (
          <div style={{ marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 8 }}>SHARED WITH</div>
            {(job.shared_with_emails || []).length === 0 && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Not shared with anyone yet.</div>}
            {(job.shared_with_emails || []).map(e => (
              <div key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 20, padding: '3px 10px', marginRight: 6, marginBottom: 6, fontSize: 12, color: '#e2e8f0' }}>
                {(() => { const u = allUsers.find(u => u.email?.toLowerCase() === e); return u ? (u.username || u.email) : e; })()}
                <span onClick={() => handleShare('remove', e)} style={{ cursor: 'pointer', color: '#f87171', fontSize: 14, lineHeight: 1 }}>×</span>
              </div>
            ))}
            <div style={{ marginTop: 8 }}>
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search user by name or email…"
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 7, color: '#fff', padding: '6px 10px', fontSize: 12.5, outline: 'none', marginBottom: 4 }} />
              {userSearch.trim() && (
                <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 7, maxHeight: 160, overflowY: 'auto' }}>
                  {allUsers.filter(u => {
                    const q = userSearch.toLowerCase();
                    const already = (job.shared_with_emails || []).map(e => e.toLowerCase());
                    return !already.includes((u.email || '').toLowerCase()) &&
                      ((u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
                  }).map(u => (
                    <div key={u.id} onClick={() => { handleShare('add', u.email); setUserSearch(''); }}
                      style={{ padding: '7px 12px', cursor: 'pointer', fontSize: 12.5, color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <span style={{ fontWeight: 600 }}>{u.username}</span>
                      <span style={{ color: '#64748b', marginLeft: 8 }}>{u.email}</span>
                    </div>
                  ))}
                  {allUsers.filter(u => {
                    const q = userSearch.toLowerCase();
                    const already = (job.shared_with_emails || []).map(e => e.toLowerCase());
                    return !already.includes((u.email || '').toLowerCase()) &&
                      ((u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q));
                  }).length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: '#64748b' }}>No matching users</div>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && <div style={{ marginBottom: 16 }}><AlertBox type="error" onClose={() => setError(null)}>{error}</AlertBox></div>}

      {/* Status card */}
      <div style={{ ...card(), marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 0 }}>
          {[
            { label: 'Status', value: <StatusChip status={job.status} /> },
            {
              label: 'Progress',
              value: (job.status === 'processing' || job.status === 'sampling' || job.status === 'rerunning')
                ? <ProgressBar value={job.progress} message={job.progress_message} />
                : <span style={{ fontSize: 14, color: '#334155' }}>{job.processed_rows} / {job.total_rows} rows</span>
            },
            { label: 'Created', value: <span style={{ fontSize: 13, color: '#334155' }}>{formatDate(job.created_at)}</span> },
            { label: 'Completed', value: <span style={{ fontSize: 13, color: '#334155' }}>{formatDate(job.completed_at)}</span> },
          ].map((item, idx) => (
            <div key={idx} style={{ padding: '16px 20px', borderRight: idx < 3 ? '1px solid #f1f5f9' : 'none' }}>
              <div style={labelCss}>{item.label}</div>
              {item.value}
            </div>
          ))}
        </div>

        {/* AI Usage */}
        {(job.total_input_tokens > 0 || job.total_output_tokens > 0) && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '14px 20px' }}>
            <div style={labelCss}>AI Usage</div>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
              {[
                { label: 'Input Tokens', value: (job.total_input_tokens || 0).toLocaleString() },
                { label: 'Output Tokens', value: (job.total_output_tokens || 0).toLocaleString() },
                { label: 'API Calls', value: (job.total_api_calls || 0).toLocaleString() },
                { label: 'Est. Cost', value: `$${parseFloat(job.estimated_cost || 0).toFixed(4)}` },
              ].map(s => (
                <div key={s.label}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status-specific alerts */}
        {job.status === 'failed' && job.error_message && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '14px 20px' }}><AlertBox type="error">{job.error_message}</AlertBox></div>
        )}
        {job.status === 'draft_codelist_ready' && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '14px 20px' }}>
            <AlertBox type="warning">Draft codelist ready for review. Edit codes in the "Draft Codelist" tab, then click "Run Full Coding".</AlertBox>
          </div>
        )}
        {job.status === 'sampling' && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '14px 20px' }}>
            <AlertBox type="info">Sampling responses to build a draft codelist. This usually takes a minute or two.</AlertBox>
          </div>
        )}

        {/* Merge level picker */}
        {job.status === 'codelist_merge_pending' && (
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Codelist Generated — Choose Your Preferences</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>{job.progress_message}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* Merge level */}
              <div>
                <div style={labelCss}>Merge Level</div>
                {[
                  { value: 'none',   label: 'None',   desc: 'Keep all codes as generated. No merging.' },
                  { value: 'strict', label: 'Strict', desc: 'Merge only exact/trivial duplicates.' },
                  { value: 'medium', label: 'Medium', desc: 'Merge near-duplicates (same concept, different words).' },
                  { value: 'light',  label: 'Light',  desc: 'Broader consolidation — merge closely related themes.' },
                ].map(opt => (
                  <div key={opt.value} onClick={() => setMergeLevel(opt.value)} style={{ padding: '10px 14px', marginBottom: 8, borderRadius: 8, cursor: 'pointer', border: `2px solid ${mergeLevel === opt.value ? '#0f172a' : '#e2e8f0'}`, background: mergeLevel === opt.value ? '#f8fafc' : '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: mergeLevel === opt.value ? 700 : 400, color: '#0f172a' }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
              {/* Phrasing */}
              <div>
                <div style={labelCss}>Label Phrasing</div>
                {[
                  { value: 'respondent',   label: "Respondent's own words", desc: 'Labels reflect what respondents actually said — specific, personal, emotional.' },
                  { value: 'professional', label: 'Professional analyst language', desc: 'Clean, neutral, concise category-style labels.' },
                ].map(opt => (
                  <div key={opt.value} onClick={() => setPhrasing(opt.value)} style={{ padding: '10px 14px', marginBottom: 8, borderRadius: 8, cursor: 'pointer', border: `2px solid ${phrasing === opt.value ? '#3b82f6' : '#e2e8f0'}`, background: phrasing === opt.value ? '#eff6ff' : '#fff' }}>
                    <div style={{ fontSize: 13, fontWeight: phrasing === opt.value ? 700 : 400, color: '#0f172a' }}>{opt.label}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>
            <button style={btn('#f59e0b', '#fff', { marginTop: 14, opacity: applyingMerge ? 0.6 : 1 })} onClick={handleApplyMerge} disabled={applyingMerge}>
              <Play size={13} /> {applyingMerge ? 'Applying…' : 'Apply & Finalize Codelist'}
            </button>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ display: 'flex', overflowX: 'auto', borderBottom: '1px solid #e2e8f0' }}>
          {TABS.map((tab, idx) => (
            <button key={idx} disabled={tab.disabled} onClick={() => setActiveTab(idx)} style={{
              position: 'relative', padding: '11px 18px', border: 'none', borderBottom: `2.5px solid ${activeTab === idx ? '#0f172a' : 'transparent'}`,
              background: activeTab === idx ? '#f8fafc' : 'transparent', cursor: tab.disabled ? 'not-allowed' : 'pointer',
              fontSize: 12.5, fontWeight: activeTab === idx ? 700 : 400, color: tab.disabled ? '#94a3b8' : activeTab === idx ? '#0f172a' : '#64748b',
              whiteSpace: 'nowrap',
            }}>
              {tab.label}
              {tab.badge != null && (
                <span style={{ marginLeft: 5, background: tab.badgeColor, color: '#fff', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 5px', verticalAlign: 'middle' }}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab 0: Configuration */}
        {activeTab === 0 && (
          <div style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div>
                <div style={labelCss}>Data File</div>
                <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{job.data_file_name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Sheet: {job.data_sheet_name}</div>
              </div>
              <div>
                <div style={labelCss}>Codelist File</div>
                <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{job.codelist_file_name || 'AI Generated'}</div>
                {job.codelist_sheet_name && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Sheet: {job.codelist_sheet_name}</div>}
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={labelCss}>Target Columns</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                {(job.target_columns || []).map(col => <Chip key={col} label={col} color="primary" />)}
              </div>
            </div>

            {/* Column Group Assignments */}
            {(() => {
              const groups = job.column_groups || [];
              const colToGroup = {};
              groups.forEach((g, gi) => (g.target_columns || []).forEach(c => { colToGroup[c] = gi; }));
              const unassigned = (job.target_columns || []).filter(c => colToGroup[c] === undefined);

              const applyAssignments = async () => {
                if (Object.keys(pendingAssign).length === 0) return;
                setSavingAssign(true);
                try {
                  const updatedGroups = groups.map((g, gi) => ({
                    ...g,
                    target_columns: [
                      ...(g.target_columns || []),
                      ...Object.entries(pendingAssign).filter(([, v]) => v === gi).map(([c]) => c),
                    ],
                  }));
                  await api.patch(`oe_coding/jobs/${jobId}/`, { column_groups: updatedGroups });
                  await fetchJob();
                  setPendingAssign({});
                  if (window.confirm('Column assignments saved. Rerun the job now to recode with the correct codelists?')) {
                    await api.patch(`oe_coding/jobs/${jobId}/`, { status: 'failed' });
                    await api.post(`oe_coding/jobs/${jobId}/rerun/`);
                    await fetchJob();
                  }
                } catch (err) { alert('Failed: ' + (err.response?.data?.error || err.message)); }
                finally { setSavingAssign(false); }
              };

              if (groups.length === 0) return null;
              return (
                <div style={{ marginBottom: 20 }}>
                  <div style={labelCss}>Column Group Assignments</div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', fontSize: 12.5 }}>
                    {(job.target_columns || []).map(col => {
                      const gi = colToGroup[col] ?? pendingAssign[col];
                      const g = gi !== undefined ? groups[gi] : null;
                      return (
                        <div key={col} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px', borderBottom: '1px solid #f1f5f9', background: g ? '#fff' : '#fff7ed' }}>
                          <span style={{ fontWeight: 600, width: 80, color: g ? '#0f172a' : '#c2410c' }}>{col}</span>
                          {g ? (
                            <span style={{ color: '#16a34a', flex: 1 }}>✓ {g.header || `Group ${(colToGroup[col] ?? 0) + 1}`}</span>
                          ) : (
                            <>
                              <span style={{ color: '#c2410c', flex: 1 }}>⚠ Unassigned — will get all Others</span>
                              <select value={pendingAssign[col] ?? ''} onChange={e => setPendingAssign(p => ({ ...p, [col]: Number(e.target.value) }))}
                                style={{ fontSize: 12, border: '1px solid #e2e8f0', borderRadius: 5, padding: '3px 6px' }}>
                                <option value="">Assign to group…</option>
                                {groups.map((g, gi) => <option key={gi} value={gi}>{g.header || `Group ${gi + 1}`}</option>)}
                              </select>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {unassigned.length > 0 && (
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: '#c2410c' }}>{unassigned.length} column(s) unassigned</span>
                      {Object.keys(pendingAssign).length > 0 && (
                        <button style={btn('#0f172a', '#fff', { fontSize: 12, padding: '5px 14px', opacity: savingAssign ? 0.5 : 1 })} onClick={applyAssignments} disabled={savingAssign}>
                          {savingAssign ? 'Saving…' : 'Save & Rerun'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {job.custom_instructions?.length > 0 && (
              <div>
                <div style={labelCss}>Custom Instructions</div>
                {job.custom_instructions.map((inst, i) => <div key={i} style={{ fontSize: 13, color: '#475569', marginBottom: 4 }}>• {inst}</div>)}
              </div>
            )}
          </div>
        )}

        {/* Tab 1: Draft Codelist */}
        {activeTab === 1 && (
          <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}><AlertBox type="info">Review and edit the AI-generated draft codelist. Add, edit, or delete codes. Once satisfied, click "Run Full Coding".</AlertBox></div>

            {['draft_codelist_ready', 'codelist_merge_pending'].includes(job?.status) && (
              <div style={{ marginBottom: 16, padding: '14px 18px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#f8fafc' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 10 }}>Re-apply Merge & Phrasing</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select style={inp({ width: 'auto', minWidth: 180, padding: '6px 10px' })} value={mergeLevel} onChange={e => setMergeLevel(e.target.value)}>
                    <option value="none">None — keep all</option>
                    <option value="strict">Strict — exact duplicates only</option>
                    <option value="medium">Medium — ~30% of responses</option>
                    <option value="light">Light — ~20% of responses</option>
                  </select>
                  <select style={inp({ width: 'auto', minWidth: 200, padding: '6px 10px' })} value={phrasing} onChange={e => setPhrasing(e.target.value)}>
                    <option value="respondent">Respondent's own words</option>
                    <option value="professional">Professional analyst</option>
                  </select>
                  <button style={btn('transparent', '#475569', { fontSize: 12, padding: '6px 14px', opacity: applyingMerge ? 0.6 : 1 })} onClick={handleApplyMerge} disabled={applyingMerge}>{applyingMerge ? 'Applying…' : 'Re-apply'}</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Draft Codelist ({editableCodelists.length} codes)</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn('transparent', '#475569', { fontSize: 12, padding: '6px 12px' })} onClick={handleDownloadDraftCodelist} disabled={editableCodelists.length === 0 && codelists.length === 0}><Download size={12} /> Download</button>
                <button style={btn('transparent', '#475569', { fontSize: 12, padding: '6px 12px' })} onClick={handleAddDraftCode} disabled={job.status !== 'draft_codelist_ready'}><Plus size={12} /> Add Code</button>
                <button style={btn('#0f172a', '#fff', { fontSize: 12, padding: '6px 12px', opacity: (!hasUnsavedChanges || job.status !== 'draft_codelist_ready') ? 0.4 : 1 })} onClick={handleSaveDraftCodelist} disabled={!hasUnsavedChanges || job.status !== 'draft_codelist_ready'}><Save size={12} /> Save Changes</button>
              </div>
            </div>

            {editableCodelists.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>No codes yet. The codelist will appear after sampling completes.</div>
            ) : (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', maxHeight: 420 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0 }}>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {['Code', 'Label', 'Column Group', ...(job.status === 'draft_codelist_ready' ? ['Actions'] : [])].map(h => (
                          <th key={h} style={{ padding: '9px 14px', textAlign: h === 'Actions' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {editableCodelists.map(entry => (
                        <tr key={entry.code} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 14px', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{entry.code}</td>
                          <td style={{ padding: '8px 14px', color: '#334155' }}>{entry.codelist}</td>
                          <td style={{ padding: '8px 14px', color: '#94a3b8' }}>{entry.column_group}</td>
                          {job.status === 'draft_codelist_ready' && (
                            <td style={{ padding: '8px 14px', textAlign: 'right' }}>
                              <button onClick={() => handleEditDraftCode(entry)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', padding: 3 }}><Edit2 size={13} /></button>
                              <button onClick={() => handleDeleteDraftCode(entry.code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f43f5e', padding: 3 }}><Trash2 size={13} /></button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {sampleResults.length > 0 && (
              <div style={{ marginTop: 28 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Sample Coded Results ({sampleResults.length} responses)</div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>Preview of how sample responses were coded during Stage 1.</div>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ overflowY: 'auto', maxHeight: 320 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead style={{ position: 'sticky', top: 0 }}>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          {['ID', 'Column', 'Original Text', 'Assigned Codes'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {sampleResults.map((result, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{result.unique_id}</td>
                            <td style={{ padding: '7px 12px', color: '#64748b' }}>{result.column_name}</td>
                            <td style={{ padding: '7px 12px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155' }} title={result.original_text}>{result.original_text}</td>
                            <td style={{ padding: '7px 12px' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {(result.code_labels || []).map((cl, i) => <Chip key={i} label={`${cl.code}: ${cl.label}`} />)}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 2: Codelists */}
        {activeTab === 2 && (
          <div style={{ padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Codelists ({codelists.length} entries)</div>
            {codelists.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>No codelists yet. Start the job to generate or load codelists.</div>
            ) : (
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ overflowY: 'auto', maxHeight: 480 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ position: 'sticky', top: 0 }}>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        {['Code', 'Codelist', 'Super NETS', 'NETS', 'Column Group', 'Source'].map(h => <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {codelists.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 14px', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{item.code}</td>
                          <td style={{ padding: '8px 14px', color: '#334155' }}>{item.codelist}</td>
                          <td style={{ padding: '8px 14px', color: '#94a3b8' }}>{item.super_nets || '—'}</td>
                          <td style={{ padding: '8px 14px', color: '#94a3b8' }}>{item.nets || '—'}</td>
                          <td style={{ padding: '8px 14px', color: '#94a3b8' }}>{item.column_group || '—'}</td>
                          <td style={{ padding: '8px 14px' }}>
                            <Chip label={item.is_ai_generated ? 'AI Generated' : 'Predefined'} color={item.is_ai_generated ? 'primary' : 'default'} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 3: Discovered Themes */}
        {activeTab === 3 && (
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Discovered Themes ({discoveredThemes.length} pending)</span>
              <button style={btn('transparent', '#475569', { fontSize: 12, padding: '6px 12px' })} onClick={fetchDiscoveredThemes}><RefreshCw size={12} /> Refresh</button>
            </div>

            {discoveredThemes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 12 }}>💡</div>
                <div style={{ fontSize: 13 }}>No new themes discovered yet. New themes appear here after processing responses that don't match existing codes.</div>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 14 }}><AlertBox type="info">Review each theme and approve (add to codelist) or reject.</AlertBox></div>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ overflowY: 'auto', maxHeight: 480 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0 }}>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          {['Suggested Theme', 'Responses', 'Sample Responses', 'Column', 'Actions'].map(h => <th key={h} style={{ padding: '9px 14px', textAlign: h === 'Responses' || h === 'Actions' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {discoveredThemes.map(theme => (
                          <tr key={theme.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '10px 14px', fontWeight: 600, color: '#334155' }}>{theme.suggested_label}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center' }}><Chip label={theme.response_count} color="primary" /></td>
                            <td style={{ padding: '10px 14px', maxWidth: 300 }}>
                              {(theme.sample_responses || []).slice(0, 2).map((s, i) => (
                                <div key={i} style={{ fontSize: 11.5, color: '#64748b', fontStyle: 'italic', marginBottom: 3 }}>"{s.length > 80 ? s.substring(0, 80) + '…' : s}"</div>
                              ))}
                              {(theme.sample_responses || []).length > 2 && <div style={{ fontSize: 11, color: '#94a3b8' }}>…and {theme.sample_responses.length - 2} more</div>}
                            </td>
                            <td style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 12 }}>{theme.column_group || '—'}</td>
                            <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                              <div style={{ display: 'inline-flex', gap: 4 }}>
                                <button title="Approve & Add to Codelist" onClick={() => handleOpenApproveDialog(theme)} style={{ background: '#f0fdf4', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#10b981', padding: '4px 6px' }}><Check size={14} /></button>
                                <button title="Reject Theme" onClick={() => handleRejectTheme(theme)} style={{ background: '#fff1f2', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#f43f5e', padding: '4px 6px' }}><X size={14} /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab 4: Emotion Analysis */}
        {activeTab === 4 && (() => {
          const EMOTION_COLORS = {
            Happy: '#4caf50', Satisfied: '#66bb6a', Grateful: '#81c784', Excited: '#ff9800',
            Hopeful: '#29b6f6', Proud: '#7b1fa2', Trusting: '#26a69a',
            Frustrated: '#ef5350', Angry: '#c62828', Disappointed: '#e57373',
            Confused: '#ffa726', Anxious: '#ef6c00', Sad: '#5c6bc0',
            Neutral: '#9e9e9e', Indifferent: '#bdbdbd', Informative: '#78909c',
          };
          const getEmotionColor = (label) => EMOTION_COLORS[label] || '#9e9e9e';
          const POSITIVE_EMOTIONS = ['Happy', 'Satisfied', 'Grateful', 'Excited', 'Hopeful', 'Proud', 'Trusting'];
          const NEGATIVE_EMOTIONS = ['Frustrated', 'Angry', 'Disappointed', 'Confused', 'Anxious', 'Sad'];
          const NEUTRAL_EMOTIONS = ['Neutral', 'Indifferent', 'Informative'];
          const getEmotionGroup = (label) => POSITIVE_EMOTIONS.includes(label) ? 'positive' : NEGATIVE_EMOTIONS.includes(label) ? 'negative' : 'neutral';

          return (
            <div style={{ padding: 24 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>Emotion Analysis</div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
                <select style={inp({ width: 'auto', minWidth: 240, padding: '8px 12px' })} value={sentimentColumn} onChange={e => setSentimentColumn(e.target.value)}>
                  <option value="">— select column —</option>
                  {(job.target_columns || []).map(col => <option key={col} value={col}>{col}</option>)}
                </select>
                <button style={btn('#0f172a', '#fff', { opacity: !sentimentColumn || sentimentRunning ? 0.5 : 1 })} onClick={handleRunSentiment} disabled={!sentimentColumn || sentimentRunning}>
                  {sentimentRunning ? 'Analyzing…' : 'Run Emotion Analysis'}
                </button>
                {(job.target_columns || []).length > 1 && (
                  <button style={btn('transparent', '#475569', { opacity: sentimentRunning ? 0.5 : 1 })} onClick={async () => {
                    const cols = job.target_columns || [];
                    for (const col of cols) {
                      setSentimentColumn(col); setSentimentRunning(true);
                      try {
                        await api.post(`oe_coding/jobs/${jobId}/run-sentiment/`, { column_name: col });
                        await new Promise(resolve => {
                          const poll = setInterval(async () => {
                            try {
                              const res = await api.get(`oe_coding/jobs/${jobId}/sentiment/?column_name=${encodeURIComponent(col)}`);
                              if (!(res.data.progress_message || '').includes('Running sentiment')) { clearInterval(poll); resolve(); }
                            } catch { clearInterval(poll); resolve(); }
                          }, 5000);
                        });
                      } catch { /* continue */ }
                    }
                    setSentimentRunning(false); fetchJob(); fetchSentiment(sentimentColumn);
                  }} disabled={sentimentRunning}>Run All Columns</button>
                )}
              </div>

              {sentimentRunning && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ background: '#e2e8f0', borderRadius: 4, height: 6, overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ height: '100%', borderRadius: 4, background: '#3b82f6', width: '60%', animation: 'pulse 1.5s infinite' }} />
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>Running emotion analysis… This may take a few minutes.</div>
                </div>
              )}

              {sentimentSummary?.by_column?.[sentimentColumn] && (() => {
                const colSummary = sentimentSummary.by_column[sentimentColumn];
                const emotionEntries = Object.entries(colSummary).filter(([k]) => k !== 'total').sort((a, b) => b[1] - a[1]);
                const total = emotionEntries.reduce((sum, [, v]) => sum + v, 0);
                const posTotal = emotionEntries.filter(([k]) => POSITIVE_EMOTIONS.includes(k)).reduce((s, [, v]) => s + v, 0);
                const negTotal = emotionEntries.filter(([k]) => NEGATIVE_EMOTIONS.includes(k)).reduce((s, [, v]) => s + v, 0);
                const neuTotal = emotionEntries.filter(([k]) => NEUTRAL_EMOTIONS.includes(k)).reduce((s, [, v]) => s + v, 0);
                const labels = emotionEntries.map(([k]) => k);
                const counts = emotionEntries.map(([, v]) => v);
                const colors = labels.map(getEmotionColor);

                const doughnutData = { labels, datasets: [{ data: counts, backgroundColor: colors, borderColor: colors, borderWidth: 1, hoverOffset: 8 }] };
                const doughnutOptions = { responsive: true, maintainAspectRatio: false, cutout: '50%', plugins: { legend: { position: 'right', labels: { padding: 12, usePointStyle: true, pointStyle: 'circle', font: { size: 12 } } }, tooltip: { callbacks: { label: (ctx) => { const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0; return ` ${ctx.label}: ${ctx.raw} (${pct}%)`; } } } } };
                const barData = { labels, datasets: [{ data: counts, backgroundColor: colors, borderRadius: 4 }] };
                const barOptions = { responsive: true, maintainAspectRatio: false, indexAxis: 'y', scales: { x: { grid: { display: false } }, y: { grid: { display: false } } }, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => { const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : 0; return ` ${ctx.raw} responses (${pct}%)`; } } } } };

                return (
                  <>
                    {/* Summary cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
                      {[
                        { label: 'Positive', count: posTotal, icon: '😊', bg: '#f0fdf4', color: '#065f46', keys: POSITIVE_EMOTIONS },
                        { label: 'Neutral', count: neuTotal, icon: '😐', bg: '#f1f5f9', color: '#475569', keys: NEUTRAL_EMOTIONS },
                        { label: 'Negative', count: negTotal, icon: '😔', bg: '#fff1f2', color: '#9f1239', keys: NEGATIVE_EMOTIONS },
                      ].map(card2 => (
                        <div key={card2.label} style={{ background: card2.bg, borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                          <div style={{ fontSize: 24 }}>{card2.icon}</div>
                          <div style={{ fontSize: 24, fontWeight: 800, color: card2.color, fontVariantNumeric: 'tabular-nums' }}>{card2.count}</div>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{card2.label} ({total > 0 ? ((card2.count / total) * 100).toFixed(1) : 0}%)</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, marginTop: 8 }}>
                            {emotionEntries.filter(([k]) => card2.keys.includes(k)).map(([k, v]) => (
                              <span key={k} style={{ background: getEmotionColor(k), color: '#fff', borderRadius: 20, fontSize: 10.5, fontWeight: 600, padding: '2px 8px' }}>{k}: {v}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Charts */}
                    <div style={{ display: 'grid', gridTemplateColumns: '5fr 7fr', gap: 16, marginBottom: 16 }}>
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 12 }}>EMOTION DISTRIBUTION</div>
                        <div style={{ height: 300 }}><Doughnut data={doughnutData} options={doughnutOptions} /></div>
                      </div>
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 12 }}>EMOTIONS BREAKDOWN — {sentimentColumn}</div>
                        <div style={{ height: Math.max(200, labels.length * 32) }}><Bar data={barData} options={barOptions} /></div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 14 }}>Total responses analyzed: <strong>{total}</strong></div>
                  </>
                );
              })()}

              {sentimentResults.length > 0 && (
                <div>
                  <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', marginBottom: 14 }} />
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#64748b', marginBottom: 10 }}>Response Details ({sentimentResults.length})</div>
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ overflowY: 'auto', maxHeight: 360 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                        <thead style={{ position: 'sticky', top: 0 }}>
                          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            {['ID', 'Original Text', 'Translation', 'Emotion', 'Score'].map(h => <th key={h} style={{ padding: '8px 12px', textAlign: h === 'Score' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {sentimentResults.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '7px 12px', color: '#64748b' }}>{row.unique_id}</td>
                              <td style={{ padding: '7px 12px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#334155' }} title={row.original_text}>{row.original_text}</td>
                              <td style={{ padding: '7px 12px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#94a3b8' }} title={row.translation}>{row.translation || '—'}</td>
                              <td style={{ padding: '7px 12px' }}>
                                <span style={{ background: EMOTION_COLORS[row.sentiment_label] || '#9e9e9e', color: '#fff', borderRadius: 20, fontSize: 11, fontWeight: 600, padding: '2px 9px' }}>{row.sentiment_label}</span>
                              </td>
                              <td style={{ padding: '7px 12px', textAlign: 'center', color: '#64748b' }}>{row.sentiment_score != null ? row.sentiment_score.toFixed(2) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {!sentimentRunning && sentimentResults.length === 0 && !sentimentSummary && (
                <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 13 }}>Select a column and click "Run Emotion Analysis" to get started.</div>
              )}
            </div>
          );
        })()}

        {/* Tab 5: Others Review */}
        {activeTab === 5 && (
          <div style={{ padding: 24 }}>
            {/* AI Recode */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 18, marginBottom: 20, background: '#f8fafc' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>AI Recode</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 14 }}>Re-process specific codes through AI using the saved codelist. For predefined codelists, keep "Auto-create" off so responses map to existing codes only.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label style={{ ...labelCss }}>Codes to recode (comma-separated, e.g. 990 or 990,999)</label>
                  <input style={inp()} value={recodeCodes} onChange={e => setRecodeCodes(e.target.value)} placeholder="990" />
                </div>
                <div>
                  <label style={{ ...labelCss }}>Custom instructions for AI (optional)</label>
                  <textarea style={inp({ minHeight: 60, resize: 'vertical' })} value={recodePrompt} onChange={e => setRecodePrompt(e.target.value)} placeholder='e.g. "Translate Arabic brand names and match to English codelist entries"' />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#475569' }}>
                  <Toggle checked={recodeAutoCreate} onChange={e => setRecodeAutoCreate(e.target.checked)} />
                  Auto-create new codes for unmatched brands (adds to codelist)
                </label>
                <div>
                  <button style={btn('#f59e0b', '#fff', { opacity: recodeRunning || job?.status === 'rerunning' ? 0.5 : 1 })} onClick={handleRecodeByCode} disabled={recodeRunning || job?.status === 'rerunning'}>
                    {recodeRunning || job?.status === 'rerunning' ? 'Processing…' : 'Recode Selected Codes'}
                  </button>
                </div>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', marginBottom: 16 }} />

            {/* Manual Review */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Manual Review (Code 990)</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btn('transparent', '#475569', { fontSize: 12, padding: '6px 12px', opacity: othersLoading ? 0.5 : 1 })} onClick={fetchOthersResponses} disabled={othersLoading}>{othersLoading ? 'Loading…' : 'Load Others'}</button>
                {Object.keys(recodeActions).length > 0 && (
                  <button style={btn('#0f172a', '#fff', { fontSize: 12, padding: '6px 12px' })} onClick={handleSubmitRecodes}>Apply {Object.keys(recodeActions).length} Recode(s)</button>
                )}
              </div>
            </div>

            {othersData && (
              <>
                <div style={{ marginBottom: 14 }}><AlertBox type="info">{othersData.others_count} responses coded as Others ({othersData.unique_texts} unique texts). Assign existing codes or create new ones below.</AlertBox></div>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ overflowY: 'auto', maxHeight: 440 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ position: 'sticky', top: 0 }}>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          {['Response Text', 'Translation', 'Count', 'Columns', 'Action'].map(h => <th key={h} style={{ padding: '9px 14px', textAlign: h === 'Count' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {othersData.groups.map((group, idx) => {
                          const textKey = (group.original_text || '').trim().toLowerCase();
                          const assigned = recodeActions[textKey];
                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: assigned ? '#f0fdf4' : '#fff' }}>
                              <td style={{ padding: '9px 14px', color: '#334155' }}>{group.original_text}</td>
                              <td style={{ padding: '9px 14px', color: '#94a3b8', fontSize: 12 }}>{group.translation || '—'}</td>
                              <td style={{ padding: '9px 14px', textAlign: 'center' }}><Chip label={group.count} color="primary" /></td>
                              <td style={{ padding: '9px 14px', fontSize: 12, color: '#64748b' }}>{group.columns.join(', ')}</td>
                              <td style={{ padding: '9px 14px' }}>
                                {assigned ? (
                                  <Chip label={`Code ${assigned.code}${assigned.new_code_label ? ` (New: ${assigned.new_code_label})` : ''}`} color="success" onDelete={() => setRecodeActions(prev => { const n = { ...prev }; delete n[textKey]; return n; })} />
                                ) : (
                                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                    <select style={inp({ width: 'auto', minWidth: 160, padding: '6px 10px' })} value="" onChange={e => handleAssignExistingCode(textKey, group.result_ids, e.target.value)}>
                                      <option value="" disabled>Assign code…</option>
                                      {othersData.codelists.map(c => <option key={c.code} value={c.code}>{c.code}: {c.codelist}</option>)}
                                    </select>
                                    <button style={btn('transparent', '#475569', { fontSize: 12, padding: '5px 10px' })} onClick={() => handleOpenNewCodeDialog(textKey, group.original_text, group.result_ids)}>+ New</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
            {!othersData && !othersLoading && <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13 }}>Click "Load Others" to review responses coded as 990 (Others).</div>}
          </div>
        )}

        {/* Tab 6: AI Session */}
        {activeTab === 6 && (
          <div style={{ padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>🤖 AI Session Log</div>
                <div style={{ fontSize: 12.5, color: '#64748b' }}>Every prompt sent to the AI and its response. Review what happened, then add instructions to refine.</div>
              </div>
              <button style={btn('transparent', '#475569', { fontSize: 12, padding: '6px 12px', opacity: llmLoading ? 0.5 : 1 })} onClick={fetchLLMInteractions} disabled={llmLoading}><RefreshCw size={12} /> {llmLoading ? 'Loading…' : 'Load Session'}</button>
            </div>

            {llmInteractions.length === 0 && !llmLoading && (
              <div style={{ marginBottom: 20 }}><AlertBox type="info">Click "Load Session" to view all AI interactions. Interactions are recorded during processing.</AlertBox></div>
            )}

            {llmInteractions.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 12 }}>
                  <AlertBox type="success">
                    {llmInteractions.length} interaction{llmInteractions.length !== 1 ? 's' : ''} — {llmInteractions.reduce((s, i) => s + i.input_tokens + i.output_tokens, 0).toLocaleString()} total tokens
                  </AlertBox>
                </div>
                {llmInteractions.map((interaction, idx) => {
                  const typeColors = { classify: '#3b82f6', sampling: '#8b5cf6', sentiment: '#10b981', theme_discovery: '#f59e0b', recode: '#f43f5e' };
                  const totalTok = interaction.input_tokens + interaction.output_tokens;
                  return (
                    <Accordion key={interaction.id} title={
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                        <span style={{ background: typeColors[interaction.interaction_type] || '#94a3b8', color: '#fff', borderRadius: 20, fontSize: 10.5, fontWeight: 700, padding: '2px 9px' }}>{interaction.interaction_type}</span>
                        {interaction.column_name && <span style={{ fontSize: 12.5, fontWeight: 600, color: '#334155' }}>{interaction.column_name}</span>}
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>Batch {interaction.batch_number}/{interaction.total_batches}</span>
                        <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: 20, fontSize: 10.5, fontWeight: 600, padding: '2px 8px' }}>{totalTok.toLocaleString()} tokens</span>
                        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#94a3b8' }}>{new Date(interaction.created_at).toLocaleTimeString()}</span>
                      </div>
                    }>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                            PROMPT <span style={{ background: '#f1f5f9', color: '#475569', borderRadius: 20, fontSize: 10, fontWeight: 600, padding: '1px 7px', marginLeft: 4 }}>{interaction.input_tokens} tokens</span>
                          </div>
                          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', maxHeight: 280, overflow: 'auto', background: '#fafafa' }}>
                            <pre style={{ margin: 0, fontSize: 11, fontFamily: 'monospace', lineHeight: 1.5, whiteSpace: 'pre-wrap', color: '#334155' }}>{interaction.prompt_text}</pre>
                          </div>
                        </div>
                        <div>
                          <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>
                            RESPONSE <span style={{ background: '#eff6ff', color: '#1e40af', borderRadius: 20, fontSize: 10, fontWeight: 600, padding: '1px 7px', marginLeft: 4 }}>{interaction.output_tokens} tokens</span>
                          </div>
                          <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px', maxHeight: 280, overflow: 'auto', background: '#f0f8ff' }}>
                            <pre style={{ margin: 0, fontSize: 11, fontFamily: 'monospace', lineHeight: 1.5, whiteSpace: 'pre-wrap', color: '#334155' }}>{interaction.response_text}</pre>
                          </div>
                        </div>
                      </div>
                    </Accordion>
                  );
                })}
              </div>
            )}

            {/* Refine & Rerun */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 18, background: '#f8fafc' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>Refine & Rerun</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 14 }}>Type a corrective instruction based on what you observed above. It will be appended to the job's instructions and the job will rerun.</div>
              {job?.custom_instructions?.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <AlertBox type="info">
                    <strong>Current instructions:</strong>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                      {job.custom_instructions.map((inst, i) => <li key={i} style={{ fontSize: 12.5 }}>{inst}</li>)}
                    </ul>
                  </AlertBox>
                </div>
              )}
              <textarea style={inp({ minHeight: 80, resize: 'vertical', marginBottom: 10 })} placeholder='e.g. "Treat التجارة as Trade (commercial sector) — it is not N/A" or "Riyad Bank and Al-Rajhi Bank are different entities, never merge them"' value={sessionInstruction} onChange={e => setSessionInstruction(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={btn('#0f172a', '#fff', { opacity: !sessionInstruction.trim() || sessionRerunning ? 0.4 : 1 })} onClick={handleRerunWithInstruction} disabled={!sessionInstruction.trim() || sessionRerunning}>
                  <Play size={13} /> {sessionRerunning ? 'Starting rerun…' : 'Append Instruction & Rerun'}
                </button>
                <button style={btn('transparent', '#f59e0b', { border: '1.5px solid #f59e0b', opacity: regenerating || ['processing', 'sampling'].includes(job?.status) ? 0.4 : 1 })} onClick={handleRegenerateCodelist} disabled={regenerating || ['processing', 'sampling'].includes(job?.status)}>
                  <RotateCcw size={13} /> {regenerating ? 'Regenerating…' : 'Regenerate Codelist'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Approve Theme Dialog */}
      <Modal open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}
        title="Approve Theme & Add to Codelist"
        subtitle="Add this discovered theme to the draft codelist"
        actions={<>
          <button style={{ padding: '9px 18px', border: 'none', borderRadius: 9, background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }} onClick={() => setApproveDialogOpen(false)}>Cancel</button>
          <button style={{ padding: '9px 20px', border: 'none', borderRadius: 9, background: !approveForm.code || !approveForm.label ? '#f1f5f9' : '#10b981', color: !approveForm.code || !approveForm.label ? '#94a3b8' : '#fff', cursor: !approveForm.code || !approveForm.label ? 'not-allowed' : 'pointer', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit' }} onClick={handleApproveTheme} disabled={!approveForm.code || !approveForm.label}>Approve & Add</button>
        </>}
      >
        {selectedTheme && (
          <div>
            <div style={{ marginBottom: 14 }}><AlertBox type="info">This theme was found in <strong>{selectedTheme.response_count}</strong> response(s).</AlertBox></div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Code Number *', key: 'code', type: 'number', hint: 'Unique code number for this theme' },
                { label: 'Code Label *', key: 'label', type: 'text', hint: 'Description for the codelist', multi: true },
                { label: 'Nets (Optional)', key: 'nets', type: 'text', hint: 'Group codes under a net category' },
                { label: 'Super Nets (Optional)', key: 'super_nets', type: 'text', hint: 'Higher-level grouping' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>{f.label}</label>
                  {f.multi
                    ? <textarea style={inp({ minHeight: 56, resize: 'vertical' })} value={approveForm[f.key]} onChange={e => setApproveForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
                    : <input style={inp()} type={f.type} value={approveForm[f.key]} onChange={e => setApproveForm(prev => ({ ...prev, [f.key]: e.target.value }))} />}
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 3 }}>{f.hint}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      {/* Draft Code Edit Dialog */}
      <Modal open={draftEditDialogOpen} onClose={() => setDraftEditDialogOpen(false)}
        title={editingDraftCode ? 'Edit Code' : 'Add New Code'}
        subtitle={editingDraftCode ? 'Update the code details below' : 'Add a new code to the draft codelist'}
        actions={<>
          <button style={{ padding: '9px 18px', border: 'none', borderRadius: 9, background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }} onClick={() => setDraftEditDialogOpen(false)}>Cancel</button>
          <button style={{ padding: '9px 20px', border: 'none', borderRadius: 9, background: !draftEditForm.code || !draftEditForm.codelist.trim() ? '#f1f5f9' : '#4f46e5', color: !draftEditForm.code || !draftEditForm.codelist.trim() ? '#94a3b8' : '#fff', cursor: !draftEditForm.code || !draftEditForm.codelist.trim() ? 'not-allowed' : 'pointer', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit' }} onClick={handleSaveDraftCodeEdit} disabled={!draftEditForm.code || !draftEditForm.codelist.trim()}>
            {editingDraftCode ? 'Update' : 'Add'}
          </button>
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { label: 'Code Number', key: 'code', type: 'number' },
            { label: 'Code Label', key: 'codelist', type: 'text' },
            { label: 'Column Group', key: 'column_group', type: 'text' },
          ].map(f => (
            <div key={f.key}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>{f.label}</label>
              <input style={inp()} type={f.type} value={draftEditForm[f.key]} onChange={e => setDraftEditForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
      </Modal>

      {/* New Code Dialog */}
      <Modal open={newCodeDialog.open} onClose={() => setNewCodeDialog({ open: false, textKey: '', text: '', result_ids: [] })}
        title="Create New Code"
        subtitle="Create a new code and assign it to this response"
        actions={<>
          <button style={{ padding: '9px 18px', border: 'none', borderRadius: 9, background: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 600, color: '#64748b', fontFamily: 'inherit' }} onClick={() => setNewCodeDialog({ open: false, textKey: '', text: '', result_ids: [] })}>Cancel</button>
          <button style={{ padding: '9px 20px', border: 'none', borderRadius: 9, background: !newCodeForm.code || !newCodeForm.label ? '#f1f5f9' : '#4f46e5', color: !newCodeForm.code || !newCodeForm.label ? '#94a3b8' : '#fff', cursor: !newCodeForm.code || !newCodeForm.label ? 'not-allowed' : 'pointer', fontSize: 13.5, fontWeight: 700, fontFamily: 'inherit' }} onClick={handleSaveNewCode} disabled={!newCodeForm.code || !newCodeForm.label}>Create & Assign</button>
        </>}
      >
        <div style={{ marginBottom: 14 }}><AlertBox type="info">Creating new code for: <strong>{newCodeDialog.text}</strong></AlertBox></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>Code Number</label>
            <input style={inp()} type="number" value={newCodeForm.code} onChange={e => setNewCodeForm(prev => ({ ...prev, code: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: '#475569', marginBottom: 4, display: 'block' }}>Code Label</label>
            <input style={inp()} value={newCodeForm.label} onChange={e => setNewCodeForm(prev => ({ ...prev, label: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
